<template>
  <div class="login-popup">
    <v-container
      fluid
      class="pa-0"
    > 
      <v-tabs
        v-model="tab"
        bg-color="#EE730F"
        class="mb-4"
        show-arrows
        centered
        fixed-tabs
      >
        <v-tab
          value="one"
          class="text-none"
        >
          {{ $t('login') }}
        </v-tab>
        <v-tab
          value="two"
          class="text-none"
        >
          {{ $t('register') }}
        </v-tab>
        <v-tab
          value="three"
          class="text-none"
        >
          {{ $t('forgottenPassword') }}
        </v-tab>
      </v-tabs>

      <v-card-text class="pa-4">
        <v-window v-model="tab">
          <!-- Login Form -->
          <v-window-item value="one">
            <v-form
              ref="loginForm"
              class="mt-4"
              @submit.prevent="loginUser"
            >
              <v-text-field
                v-model="userData.emailAddress"
                :label="$t('loginPopup.emailAddress')"
                :rules="[required,email]"
                required
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-4"
              />

              <v-text-field
                v-model="userData.password"
                :label="$t('loginPopup.password')"
                type="password"
                :rules="[required, minLength(8)]"
                required
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-4"
              />

              <v-btn
                color="#6DA34D"
                type="submit"
                :loading="loading"
                :disabled="isLoginDisabled || loading"
                block
                size="large"
                class="mt-4"
              >
                {{ $t('login') }}
              </v-btn>
            </v-form>
          </v-window-item>

          <!-- Register Form -->
          <v-window-item value="two">
            <v-form
              ref="registerForm"
              class="mt-4"
              @submit.prevent="registerUser"
            >
              <v-text-field
                v-model="userData.emailAddress"
                :label="$t('loginPopup.emailAddress')"
                :rules="[required,email]"
                required
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-4"
              />
  
              <v-text-field
                v-model="userData.password"
                :label="$t('loginPopup.password')"
                type="password"
                :rules="[required, minLength(8)]"
                required
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-4"
              />
  
              <v-text-field
                v-model="userData.confirmPassword"
                :label="$t('loginPopup.passwordConfirmation')"
                type="password"
                :rules="[required, minLength(8),passwordMatch]"
                required
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-4"
              />

              <v-checkbox
                v-model="tosAccepted"
                :label="$t('acceptToS')"
                class="mb-2"
              />
              <v-checkbox
                v-model="userData.subscribeToNewsletter"
                :label="$t('loginPopup.subscribeToNewsletter')"
                class="mb-4"
              />

              <v-btn
                color="#6DA34D"
                type="submit"
                :loading="loading"
                :disabled="isRegisterDisabled || loading"
                block
                size="large"
                class="mt-4"
              >
                {{ $t('register') }}
              </v-btn>
            </v-form>
          </v-window-item>

          <!-- Reset Password Form -->
          <v-window-item value="three">
            <v-form
              ref="resetForm"
              class="mt-4"
              @submit.prevent="resetPassword"
            >
              <v-text-field
                v-model="userData.emailAddress"
                :label="$t('loginPopup.emailAddress')"
                :rules="[required,email]"
                required
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-4"
              />

              <v-btn
                color="#6DA34D"
                type="submit"
                :loading="loading"
                :disabled="!email(userData.emailAddress)"
                block
                size="large"
                class="mt-4"
              >
                {{ $t('resetPassword') }}
              </v-btn>
            </v-form>
          </v-window-item>
        </v-window>

        <template v-if="oauthLoginEnabled && (tab === 'one' || tab === 'two')">
          <div class="oauth-divider my-4">
            <span>{{ $t('loginPopup.orContinueWith') }}</span>
          </div>

          <div class="oauth-buttons mx-auto">
            <div
              ref="googleButtonContainer"
              class="mb-3"
            />

            <v-btn
              variant="outlined"
              block
              height="40"
              :loading="oauthLoading"
              @click="startFacebookLogin"
            >
              <v-icon
                start
                color="#1877F2"
              >
                mdi-facebook
              </v-icon>
              {{ $t('loginPopup.continueWithFacebook') }}
            </v-btn>
          </div>
        </template>
      </v-card-text>
    </v-container>
  </div>
</template>

<script>
import userService from '@/services/userService';
import { decodeToken } from '@/services/tokenService';
import { renderGoogleButton, facebookLogin } from '@/plugins/oauthLogin';
import config from '@/config/config';

export default {
  data: () => ({
    tab: null,
    tosAccepted: false,
    loading: false,
    oauthLoading: false,
    userData: {
      emailAddress: null,
      password: null,
      confirmPassword: null,
      subscribeToNewsletter: false
    }
  }),
  computed: {
    oauthLoginEnabled() {
      return config.featureFlags.enableOauthLogin;
    },
    required() {
      return value => !!value || this.$t('formMessages.fieldIsRequired');
    },
    email() {
      return value => /.+@.+\..+/.test(value)  || this.$t('formMessages.invalidEmail');
    },
    minLength() {
      return min => value => value.length >= min || this.$t('passwordMinimumLength') + `${min}`;
    },
    passwordMatch() {
      return (this.userData.password && this.userData.confirmPassword && this.userData.password === this.userData.confirmPassword) || this.$t('passwordMismatchError');
    },
    isRegisterDisabled() {
      return !(
        this.tosAccepted && 
        this.userData.emailAddress && 
        this.email(this.userData.emailAddress) === true && 
        this.userData.password && 
        this.userData.confirmPassword && 
        this.userData.password === this.userData.confirmPassword
      );
    },
    isLoginDisabled() {
      return !(
        this.userData.emailAddress && 
        this.email(this.userData.emailAddress) === true && 
        this.userData.password && 
        this.userData.password.length >= 8
      );
    }
  },
  watch: {
    tab(newTab) {
      if (newTab === 'one' || newTab === 'two') {
        this.$nextTick(() => this.renderGoogleButtonIfVisible());
      }
    }
  },
  mounted() {
    this.$nextTick(() => this.renderGoogleButtonIfVisible());
  },
  methods: {
    resetForm() {
      this.userData = {
        emailAddress: null,
        password: null,
        confirmPassword: null,
        subscribeToNewsletter: false
      };
      this.tosAccepted = false;
      this.$refs.loginForm?.reset();
      this.$refs.registerForm?.reset();
      this.$refs.resetForm?.reset();
    },
    async registerUser() {
      this.loading = true;
      try {
        const requestData = {
          ...this.userData,
          language: this.$i18n.locale
        };
        const response = await userService.registerUser(requestData);
        this.$snackbar.add({
          type: 'success',
          text: this.$t(`backendResponses.${response.data}`)
        });
        this.$emit('close-dialog');
      } catch(error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t(`backendResponses.${error}`)
        });
      } finally {
        this.loading = false;
        this.resetForm();
      }
    },
    async resetPassword() {
      this.loading = true;
      try {
        const response = await userService.resetPassword({emailAddress:this.userData.emailAddress, language:this.$i18n.locale });
        this.$snackbar.add({
          type: 'success',
          text: this.$t(`backendResponses.${response.data}`)
        });
      } catch(error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t(`backendResponses.${error}`)
        });
      } finally {
        this.loading = false;
        this.resetForm();
      }
    },
    async loginUser() {
      this.loading = true;
      try {
        const response = await userService.loginUser(this.userData);
        if (response.data) {
          if (response.data.message !== 'SUCCESSFUL_LOGIN') {
            this.$snackbar.add({
              type: 'warning',
              text: this.$t(`backendResponses.${response.data.message}`)
            });
          }
          if (response.data.token && response.data.message === 'SUCCESSFUL_LOGIN') {
            this.$store.commit('SET_USER_TOKEN', {
              userAccessToken: response.data.token,
              snackBarType: 'success',
              snackBarText: 'SUCCESSFUL_LOGIN'
            });
            let decodedToken = decodeToken(response.data.token);
            this.$i18n.locale = decodedToken.userData.preferredLanguage;
            this.$snackbar.add({
              type: 'success',
              text: this.$t('backendResponses.SUCCESSFUL_LOGIN')
            });
            this.$emit('close-dialog');
          }
        }
      } catch (error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t(`backendResponses.${error}`)
        });
      } finally {
        this.loading = false;
        this.resetForm();
      }
    },
    renderGoogleButtonIfVisible() {
      if (!this.oauthLoginEnabled || !this.$refs.googleButtonContainer) return;
      renderGoogleButton(this.$refs.googleButtonContainer, (idToken) => {
        this.handleOauthLogin('google', idToken);
      });
    },
    startFacebookLogin() {
      this.oauthLoading = true;
      facebookLogin(
        (accessToken) => this.handleOauthLogin('facebook', accessToken),
        () => {
          this.oauthLoading = false;
        }
      );
    },
    async handleOauthLogin(provider, token) {
      this.oauthLoading = true;
      try {
        const response = await userService.oauthLogin({
          provider,
          token,
          language: this.$i18n.locale,
          subscribeToNewsletter: false
        });
        if (response.data && response.data.token && response.data.message === 'SUCCESSFUL_LOGIN') {
          this.$store.commit('SET_USER_TOKEN', {
            userAccessToken: response.data.token,
            snackBarType: 'success',
            snackBarText: 'SUCCESSFUL_LOGIN'
          });
          let decodedToken = decodeToken(response.data.token);
          this.$i18n.locale = decodedToken.userData.preferredLanguage;
          this.$snackbar.add({
            type: 'success',
            text: this.$t('backendResponses.SUCCESSFUL_LOGIN')
          });
          this.$emit('close-dialog');
        } else if (response.data && response.data.message === 'EMAIL_NOT_PROVIDED_BY_PROVIDER') {
          this.$snackbar.add({
            type: 'error',
            text: this.$t('loginPopup.oauthEmailMissing')
          });
        } else {
          this.$snackbar.add({
            type: 'error',
            text: this.$t('loginPopup.oauthFailed')
          });
        }
      } catch (error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('loginPopup.oauthFailed')
        });
      } finally {
        this.oauthLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.login-popup {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.v-container {
  max-width: 100% !important;
  padding: 0 !important;
}

.v-card-text {
  padding: 16px !important;
}

.oauth-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.oauth-divider::before,
.oauth-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.oauth-divider span {
  padding: 0 12px;
}

.oauth-buttons {
  max-width: 360px;
}

.v-tabs-slider {
  width: 100%;
  height: 4px !important;
}

.v-tabs-slider-wrapper {
  width: 100%;
  height: 4px !important;
}

.v-tab--selected {
  font-weight: 700;
  background-color: #F5A361 !important;
}

@media (max-width: 600px) {
  .login-popup {
    padding: 0;
    margin: 0;
  }

  .v-tabs {
    margin-bottom: 16px;
    width: 100%;
    overflow-x: hidden;
  }

  .v-tab {
    min-width: auto;
    padding: 0 8px;
    font-size: 0.875rem;
    flex: 1;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-text-field,
  .v-checkbox,
  .v-btn {
    width: 100%;
  }

  .v-text-field {
    margin-bottom: 16px;
  }

  .v-checkbox {
    margin-bottom: 8px;
  }

  .v-btn {
    height: 48px;
  }

  :deep(.v-field__input) {
    padding: 12px 16px;
  }

  :deep(.v-field__outline) {
    --v-field-border-width: 1px;
  }
}
</style>
